import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoaderCircle, Mail, Lock, X, AlertCircle, Check, CircleDot, MailCheck } from 'lucide-react';
import { Button, Card, Input } from '../ui';
import { useAuth } from '../../auth/AuthContext';
import { getAuthAdapter } from '../../services/auth/authAdapter';
import { isFeatureEnabled } from '../../config/features';
import { TurnstileWidget, type TurnstileWidgetRef } from '../shared/TurnstileWidget';
import { clientLogger } from '@/lib/clientLogger';

// =============================================================================
// Password Validation
// =============================================================================

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One symbol (!@#$%^&*...)', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

function validatePassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  for (const req of PASSWORD_REQUIREMENTS) {
    if (!req.test(password)) {
      errors.push(req.label);
    }
  }
  return { valid: errors.length === 0, errors };
}

/** Visual password requirements checklist */
const PasswordRequirements: React.FC<{ password: string }> = ({ password }) => {
  return (
    <div className="mt-2 space-y-1.5">
      <p className="text-xs font-medium text-slate-400 mb-2">Password must have:</p>
      {PASSWORD_REQUIREMENTS.map((req) => {
        const met = req.test(password);
        return (
          <div
            key={req.label}
            className={`flex items-center gap-2 text-xs transition-colors ${
              met ? 'text-green-400' : 'text-slate-500'
            }`}
          >
            {met ? (
              <Check size={14} className="text-green-400" />
            ) : (
              <CircleDot size={14} className="text-slate-600" />
            )}
            <span>{req.label}</span>
          </div>
        );
      })}
    </div>
  );
};

function formatAuthError(error: unknown): string {
  const code = typeof error === 'object' && error && 'code' in error ? String((error as Record<string, unknown>).code) : null;

  // Supabase error codes
  switch (code) {
    case 'INVALID_EMAIL':
      return 'Please enter a valid email address.';
    case 'INVALID_CREDENTIALS':
    case 'USER_NOT_FOUND':
      return 'Incorrect email or password.';
    case 'EMAIL_IN_USE':
      return 'That email is already in use. Try logging in instead.';
    case 'WEAK_PASSWORD':
      return 'Your password is too weak. Please use at least 8 characters with an uppercase letter and a symbol.';
    case 'TOO_MANY_REQUESTS':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'EMAIL_NOT_VERIFIED':
      return 'Please verify your email before signing in.';
    case 'NOT_CONFIGURED':
      return 'Authentication is not configured. Please contact support.';
    default:
      break;
  }

  if (error instanceof Error && error.message.trim()) return error.message;
  return 'Something went wrong. Please try again.';
}

export const AuthModal: React.FC = () => {
  const {
    authModalOpen,
    authModalMode,
    setAuthModalMode,
    closeAuthModal,
    isAuthConfigured,
    isAuthReady,
    user,
    emailVerificationRequired,
  } = useAuth();

  const titleId = useId();
  const descriptionId = useId();

  const title = useMemo(() => {
    if (user) return 'You’re signed in';
    return authModalMode === 'login' ? 'Welcome back' : 'Create your account';
  }, [authModalMode, user]);

  const subtitle = useMemo(() => {
    if (user) return user.email ? `Signed in as ${user.email}` : 'Your session is active.';
    return authModalMode === 'login'
      ? 'Sign in to access your StellarTrail workspace.'
      : 'Sign up to unlock the app experience.';
  }, [authModalMode, user]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [verificationPending, setVerificationPending] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  // Turnstile timeout: if widget doesn't produce a token within 5s, skip it
  useEffect(() => {
    if (!authModalOpen || turnstileFailed || turnstileToken) return;
    const turnstileEnabled = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (!turnstileEnabled) return;

    const timer = setTimeout(() => {
      if (!turnstileToken) {
        clientLogger.warn('[Turnstile] Timed out waiting for verification, allowing submission');
        setTurnstileFailed(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [authModalOpen, turnstileFailed, turnstileToken]);

  useEffect(() => {
    if (!authModalOpen) return;

    setFormError(null);
    setPassword('');
    setConfirmPassword('');

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuthModal();
    };
    window.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [authModalOpen, closeAuthModal]);

  useEffect(() => {
    if (!authModalOpen) return;
    setFormError(null);
    setPassword('');
    setConfirmPassword('');
    setResetEmailSent(false);
  }, [authModalMode, authModalOpen]);

  // Sync verification pending state with context
  useEffect(() => {
    if (emailVerificationRequired && !verificationPending) {
      setVerificationPending(true);
    }
  }, [emailVerificationRequired, verificationPending]);

  if (!authModalOpen) return null;

  const authNotConfiguredMessage = import.meta.env.DEV
    ? 'Auth is not configured. Add Supabase env vars in `.env.local` to enable sign-in.'
    : 'Auth is not configured. Set `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY` in deployment env, then redeploy.';

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError('Please enter your email address first.');
      return;
    }
    setSendingReset(true);
    setFormError(null);
    try {
      const authAdapter = getAuthAdapter();
      await authAdapter.sendPasswordResetEmail(trimmedEmail);
      setResetEmailSent(true);
    } catch (err) {
      setFormError(formatAuthError(err));
    } finally {
      setSendingReset(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!isAuthConfigured) {
      setFormError(authNotConfiguredMessage);
      return;
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError('Please enter your email.');
      return;
    }
    if (!password) {
      setFormError('Please enter your password.');
      return;
    }

    if (authModalMode === 'signup') {
      // Validate password requirements
      const { valid, errors } = validatePassword(password);
      if (!valid) {
        setFormError(`Password requirements not met: ${errors.join(', ')}`);
        return;
      }
      if (password !== confirmPassword) {
        setFormError('Passwords do not match.');
        return;
      }
    }

    // Turnstile bot verification (skip when site key not configured or widget failed to load)
    const turnstileEnabled = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (turnstileEnabled && !turnstileToken && !turnstileFailed) {
      setFormError('Please complete the verification.');
      return;
    }

    if (turnstileEnabled && turnstileToken) {
      try {
        const verifyRes = await fetch('/api/auth/turnstile-verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken }),
        });
        if (!verifyRes.ok) {
          const data = await verifyRes.json().catch(() => ({}));
          setFormError(data.error || 'Bot verification failed. Please try again.');
          turnstileRef.current?.reset();
          setTurnstileToken(null);
          return;
        }
      } catch {
        setFormError('Verification service unavailable. Please try again.');
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        return;
      }
    }

    try {
      setSubmitting(true);
      const authAdapter = getAuthAdapter();

      if (authModalMode === 'login') {
        const user = await authAdapter.signInWithEmail(trimmedEmail, password);
        // Check if email is verified
        if (!user.emailVerified) {
          setVerificationPending(true);
          return;
        }
        closeAuthModal();
      } else {
        // Create account - verification email sent automatically by adapter
        await authAdapter.signUpWithEmail(trimmedEmail, password);
        // Always show verification screen after signup — even if Supabase
        // auto-confirms, this is the safest UX (user checks email)
        setVerificationPending(true);
        setVerificationSent(true);
      }
    } catch (err) {
      setFormError(formatAuthError(err));
    } finally {
      setSubmitting(false);
      // Tokens are single-use; reset for next attempt
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  const handleResendVerification = async () => {
    // Get the email from the current user or the form input
    const userEmail = user?.email || email.trim();
    if (!userEmail) {
      setFormError('No email address available. Please enter your email.');
      return;
    }

    setResendingVerification(true);
    setFormError(null);

    try {
      const authAdapter = getAuthAdapter();
      await authAdapter.resendVerificationEmail(userEmail);
      setVerificationSent(true);
      // Reset the "sent" indicator after a few seconds
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (err) {
      const code = typeof err === 'object' && err && 'code' in err ? String((err as Record<string, unknown>).code) : null;
      if (code === 'auth/too-many-requests' || code === 'TOO_MANY_REQUESTS') {
        setFormError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setFormError('Failed to send verification email. Please try again.');
      }
    } finally {
      setResendingVerification(false);
    }
  };

  const handleCheckVerification = async () => {
    setSubmitting(true);
    setFormError(null);

    try {
      const authAdapter = getAuthAdapter();
      const isVerified = await authAdapter.isEmailVerified();

      if (isVerified) {
        setVerificationPending(false);
        closeAuthModal();
        window.location.reload();
      } else {
        setFormError('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      setFormError('Failed to check verification status. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeAuthModal();
      }}
      aria-hidden={!authModalOpen}
    >
      <div className="w-full max-w-lg">
        <div role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} data-testid="auth-modal">
          <Card hoverEffect={false} className="p-0 overflow-hidden">
          <div className="p-6 border-b border-slate-800 bg-gradient-to-b from-[#0b0d11] to-st-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-black tracking-[0.25em] uppercase text-st-primary">
                    StellarTrail
                  </span>
                  <span className="text-[11px] font-bold tracking-widest uppercase text-slate-500">
                    Secure Access
                  </span>
                </div>
                <h2 id={titleId} className="text-2xl sm:text-3xl font-black text-white tracking-tight mt-3">
                  {title}
                </h2>
                <p id={descriptionId} className="text-st-muted mt-2">
                  {subtitle}
                </p>
              </div>

              <button
                type="button"
                onClick={closeAuthModal}
                className="rounded-md p-2 text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {!user ? (
              <div className="mt-6 bg-black/20 rounded-lg p-1 grid grid-cols-2 gap-1">
                <button
                  type="button"
                  onClick={() => setAuthModalMode('login')}
                  className={[
                    'rounded-md px-3 py-2 text-xs font-black tracking-widest uppercase transition-colors',
                    authModalMode === 'login' ? 'bg-st-primary text-white' : 'text-slate-300 hover:text-white',
                  ].join(' ')}
                  aria-pressed={authModalMode === 'login'}
                >
                  Log in
                </button>
                <button
                  type="button"
                  onClick={() => setAuthModalMode('signup')}
                  className={[
                    'rounded-md px-3 py-2 text-xs font-black tracking-widest uppercase transition-colors',
                    authModalMode === 'signup' ? 'bg-st-primary text-white' : 'text-slate-300 hover:text-white',
                  ].join(' ')}
                  aria-pressed={authModalMode === 'signup'}
                >
                  Sign up
                </button>
              </div>
            ) : null}
          </div>

          <div className="p-6">
            {!isAuthConfigured ? (
              <div className="rounded-lg border border-slate-800 bg-black/20 p-4">
                <p className="text-white font-bold">Auth is not configured.</p>
                <p className="text-st-muted mt-2 text-sm">
                  {import.meta.env.DEV ? (
                    <>
                      Add Supabase env vars in <code className="text-slate-300">.env.local</code> and restart dev.
                    </>
                  ) : (
                    <>
                      Set <code className="text-slate-300">VITE_SUPABASE_URL</code> and{' '}
                      <code className="text-slate-300">VITE_SUPABASE_ANON_KEY</code> in deployment env, then redeploy.
                    </>
                  )}
                </p>
              </div>
            ) : !isAuthReady ? (
              <div className="flex items-center gap-3 text-slate-300">
                <LoaderCircle className="animate-spin" size={18} />
                <span className="text-sm">Checking session…</span>
              </div>
            ) : verificationPending ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-st-primary/30 bg-st-primary/5 p-5">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-st-primary/10 border border-st-primary/20">
                      <MailCheck size={24} className="text-st-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-bold text-lg">Verify your email</p>
                      <p className="text-st-muted mt-2 text-sm leading-relaxed">
                        We've sent a verification link to{' '}
                        <span className="text-white font-medium">{user?.email || email}</span>.
                        Please check your inbox and click the link to activate your account.
                      </p>
                      <p className="text-slate-500 mt-2 text-xs">
                        Don't forget to check your spam folder.
                      </p>
                    </div>
                  </div>
                </div>

                {formError ? (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle size={18} className="shrink-0 mt-0.5 text-red-400" />
                      <p>{formError}</p>
                    </div>
                  </div>
                ) : null}

                {verificationSent && !formError ? (
                  <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-sm text-green-200">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-green-400" />
                      <span>Verification email sent!</span>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col gap-3">
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={handleCheckVerification}
                    disabled={submitting}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {submitting ? <LoaderCircle className="animate-spin" size={18} /> : null}
                      I've verified my email
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {resendingVerification ? <LoaderCircle className="animate-spin" size={18} /> : <Mail size={16} />}
                      Resend verification email
                    </span>
                  </Button>
                </div>

                <div className="pt-2 border-t border-slate-800">
                  <button
                    type="button"
                    className="text-xs font-medium text-slate-400 hover:text-white transition-colors"
                    onClick={() => {
                      setVerificationPending(false);
                      setAuthModalMode('login');
                    }}
                  >
                    Use a different account
                  </button>
                </div>
              </div>
            ) : user ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-800 bg-black/20 p-4">
                  <p className="text-white font-bold">You're already signed in.</p>
                  <p className="text-st-muted mt-2 text-sm">
                    You can close this window and continue using the app.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button type="button" variant="primary" onClick={closeAuthModal}>
                    Continue
                  </Button>
                  <Button type="button" variant="outline" onClick={closeAuthModal}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {formError ? (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle size={20} className="shrink-0 mt-0.5 text-red-400" />
                      <div className="flex-1">
                        <p className="font-semibold text-red-100 mb-1">Authentication Error</p>
                        <p className="text-red-200">{formError}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormError(null)}
                        className="shrink-0 rounded p-1 text-red-300 hover:text-red-100 hover:bg-red-500/20 transition-colors"
                        aria-label="Dismiss error"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : null}

                <Input
                  label="Email"
                  type="email"
                  autoComplete={authModalMode === 'login' ? 'username' : 'email'}
                  placeholder="you@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  data-testid="auth-email"
                />

                <div>
                  <Input
                    label="Password"
                    type="password"
                    autoComplete={authModalMode === 'login' ? 'current-password' : 'new-password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="auth-password"
                  />
                  {authModalMode === 'signup' && password.length > 0 && (
                    <PasswordRequirements password={password} />
                  )}
                  {authModalMode === 'login' && (
                    <div className="mt-1.5 flex items-center justify-between">
                      {resetEmailSent ? (
                        <span className="text-xs text-green-400 flex items-center gap-1.5">
                          <Check size={12} /> Reset link sent — check your inbox
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-slate-400 hover:text-white transition-colors"
                          onClick={handleForgotPassword}
                          disabled={sendingReset}
                        >
                          {sendingReset ? 'Sending...' : 'Forgot password?'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {authModalMode === 'signup' ? (
                  <div>
                    <Input
                      label="Confirm password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    {confirmPassword.length > 0 && (
                      <div className={`flex items-center gap-2 text-xs mt-2 transition-colors ${
                        password === confirmPassword ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {password === confirmPassword ? (
                          <Check size={14} className="text-green-400" />
                        ) : (
                          <X size={14} className="text-red-400" />
                        )}
                        <span>{password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
                      </div>
                    )}
                  </div>
                ) : null}

                <TurnstileWidget
                  ref={turnstileRef}
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileFailed(true)}
                />

                <Button type="submit" variant="primary" className="w-full" disabled={submitting} data-testid="auth-submit">
                  <span className="inline-flex items-center justify-center gap-2">
                    {submitting ? <LoaderCircle className="animate-spin" size={18} /> : null}
                    {authModalMode === 'login' ? 'Sign in' : 'Create account'}
                  </span>
                </Button>

                <div className="flex items-center pt-2">
                  <button
                    type="button"
                    className="text-xs font-bold tracking-widest uppercase text-slate-400 hover:text-white transition-colors inline-flex items-center gap-2"
                    onClick={() => {
                      setAuthModalMode(authModalMode === 'login' ? 'signup' : 'login');
                    }}
                  >
                    {authModalMode === 'login' ? (
                      <>
                        <Mail size={14} /> Need an account?
                      </>
                    ) : (
                      <>
                        <Lock size={14} /> Already have an account?
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
          </Card>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

